'use client'

import { useState, type ReactNode } from 'react'

import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'

interface DisclosureProps {
    /** The control's id; the region takes it suffixed `-content`, so either is a fragment. */
    readonly id: string
    /** Phrasing content only: it sits inside the control. */
    readonly summary: ReactNode
    /** Wraps the control, so the summary is a heading the control sits in. */
    readonly heading?: 'h2' | 'h3' | 'h4'
    readonly children: ReactNode
    readonly className?: string
}

/** A control that opens and closes a region below it. The region starts closed and stays in
 *  the markup while closed, so the server response carries its content. */
export function Disclosure({
    id,
    summary,
    heading: Heading,
    children,
    className,
}: DisclosureProps) {
    const [open, setOpen] = useState(false)
    const regionId = `${id}-content`
    const control = (
        <button
            type="button"
            id={id}
            aria-controls={regionId}
            aria-expanded={open}
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            className="flex min-h-10 w-full items-center justify-between gap-3 text-left"
        >
            {summary}
            <ChevronDown
                aria-hidden="true"
                className={cn('h-5 w-5 shrink-0 transition-transform', open && 'rotate-180')}
            />
        </button>
    )
    return (
        <div className={className}>
            {Heading ? <Heading>{control}</Heading> : control}
            <div id={regionId} role="region" aria-labelledby={id} hidden={!open}>
                {children}
            </div>
        </div>
    )
}
